import React, { useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router";
import { Form } from "antd";
import moment from "moment";
import axios from "axios";
import { fetchServiceTypes } from "../../../store/slices/services";
import { ProductParamsTable } from "../../../components/ProductDetails/ProductParamsTable/ProductParamsTable";
import { ProductHeader } from "../../../components/ProductDetails/ProductHeader";
import {
  activateProduct,
  cancelProduct,
  changeProductStatus,
  changeProlognOffering,
  closeProduct,
  fetchProductById,
  fetchProductVersions,
  fetchProlongOffering,
  fetchProductsBlockingTypeStatus,
  productsActions,
  changeProductQuantity,
} from "../../../store/slices/products/products";
import { toHttpFormat, toHttpFormatStartOfDay, ymdDateFormat } from "../../../lib/utils";
import { useAppDispatch, useAppSelector } from "../../..";
import { fetchServiceMode } from "../../../store/slices/rtkDictionaries";
import { ProductsOfferParams } from "../../../components/ProductDetails/ProductsOfferParams/ProductsOfferParams";
import { ProductsTarificationParams } from "../../../components/ProductDetails/ProductsTarificationParams/ProductsTarificationParams";
import { ProductsServiceProfileParams } from "../../../components/ProductDetails/ProductsServiceProfileParams/ProductsServiceProfileParams";
import { BlockingObjTypes } from "../../../lib/const/blockingObjTypes";
import { IAccountProductsContainerContext } from "../context/types";
import { IProduct, Offer } from "../../../lib/types";
import { AccountProductsContainerContext } from "../context/AccountProductsContainerContext";
import { getChargesRoute2 } from "../../../lib/routes/charging";
import { fetchProductVersionsRoute } from "../../../lib/routes/products";
import {
  aggregationRulesActions,
  fetchAggregationRulesFromVersions,
} from "../../../modules/AggregationRules/store/slice/aggregationRulesSlice";
import { AggregationRulesView } from "../../../modules/AggregationRules";
import {
  fetchOfferByIdFromVersions,
  fetchOfferServiceProfileById,
  fetchServiceRatesFromVersions,
  offeringActions,
} from "../../../store/slices/offering/offering";
import "./ProductDetailsContainer.less";

export const ProductDetailsContainer = () => {
  const dispatch = useAppDispatch();

  const serviceStatus = useAppSelector((state) => state.rtkDictionaries.serviceStatus);

  const { productId } = useParams<{ accountId: string; productId: string }>();
  const history = useHistory();
  const { search, pathname } = useLocation();
  const searchParams = new URLSearchParams(search);
  const onDate = searchParams.get("onDate");
  const productOnDate = onDate ? moment(onDate.replace(" ", "+")) : moment();

  const { product } = useAppSelector((state) => state.products);
  const offer = (useAppSelector((state) => state.offering.offer) as unknown) as Offer;

  const [productParamsForm] = Form.useForm();

  const [isSearchBlockTypes, setIsSearchBlockTypes] = useState(false);

  const loadProductBlockingTypeStatus = () => {
    axios
      .get<IProduct[]>(fetchProductVersionsRoute(productId))
      .then(({ data }) => {
        const sortedVersions = [...data].sort((a, b) =>
          moment(a.toDate).isAfter(moment(b.toDate)) ? -1 : 1
        );
        if (sortedVersions?.[0]?.toDate) {
          dispatch(
            fetchProductsBlockingTypeStatus({
              objectTypeId: BlockingObjTypes.Продукт,
              objectId: Number(productId),
              onDate: toHttpFormat(sortedVersions?.[0]?.toDate),
            })
          );
          setIsSearchBlockTypes(true);
        }
      })
      .catch((e) => {
        console.error("Ошибка загрузки версий продукта для целей определения блокировки", e);
      });
  };

  useEffect(() => {
    if (!isSearchBlockTypes && productId) {
      loadProductBlockingTypeStatus();
    }
  }, [productId]);

  // Загрузка справочников
  useEffect(() => {
    dispatch(fetchServiceTypes());
    dispatch(fetchServiceMode());
  }, []);

  const loadProduct = (newOnDate?: string) => {
    const loadOnDate = newOnDate
      ? toHttpFormat(newOnDate)
      : onDate
      ? onDate.replace(" ", "+")
      : toHttpFormat(moment());
    dispatch(
      fetchProductById({
        onDate: loadOnDate,
        productId,
      })
    );
  };

  const loadProductVersions = () => dispatch(fetchProductVersions(productId));

  const [minCloseDate, setMinCloseDate] = useState<moment.Moment | null>(null);

  // Получение начислений для расчета доступных дат закрытия продукта
  useEffect(() => {
    if (product) {
      const url = getChargesRoute2({
        agreementId: product.agreement.id,
        serviceId: product.id,
      });
      axios.get(url).then(({ data }) => {
        const maxPeriodTo = data.length
          ? moment.max(data.map((item) => moment(item.periodTo)))
          : null;

        if (maxPeriodTo) {
          setMinCloseDate(maxPeriodTo);
        }
      });
    }
  }, [product]);

  // Загрузка продукта, очистка продукта и версий по выходу
  useEffect(() => {
    loadProduct();
    return () => {
      dispatch(productsActions.cleanProduct());
      dispatch(productsActions.cleanProductversions());
      setMinCloseDate(null);
    };
  }, []);

  // Получение оффера для пролонгации, т.к. данные нужны для тултипа
  useEffect(() => {
    if (product?.prolongOfferingId && product?.fromDate) {
      dispatch(fetchProlongOffering({ id: product.prolongOfferingId, onDate: product.fromDate }));
    }
    return () => {
      dispatch(productsActions.cleanProlongOffering());
    };
  }, [product]);

  // Изменение даты закрытия продукта
  const handleCloseProduct = (toDate: string) => {
    // Меняем дату в строке запроса если закрыли продукт датой раннее той на которой зашли
    if (moment(toDate).isBefore(moment(productOnDate), "day")) {
      const newPath = [
        pathname,
        search.split("onDate=")[0],
        "onDate=",
        moment(toDate)?.format(),
      ].join("");
      history.push(newPath);

      dispatch(closeProduct({ id: Number(productId), fromDate: toDate })).then(() => {
        loadProduct(toDate);
        loadProductVersions();
      });
    } else {
      dispatch(closeProduct({ id: Number(productId), fromDate: toDate })).then(() => {
        loadProduct();
        loadProductVersions();
      });
    }
  };

  // Отмена продукта
  const handleCancelProduct = (fromDate: string) => {
    return dispatch(cancelProduct({ id: Number(productId), fromDate, hideSuccessModal: true }));
  };

  // Активация продукта
  const handleActivateProduct = (params: {
    serviceStatusId: number;
    fromDate: string;
    force?: boolean;
  }) => {
    const { fromDate, serviceStatusId, force } = params;
    const status = serviceStatus.find((item) => item.id === serviceStatusId)?.alias || "";
    return dispatch(
      activateProduct({ id: Number(productId), fromDate, status, force, hideSuccessModal: true })
    );
  };

  // Изменение статуса продукта
  const handleСhangeProductStatus = (params: {
    serviceStatusId: number;
    fromDate?: string;
    force: boolean;
    changeChildStatuses?: boolean;
  }) => {
    const { fromDate, serviceStatusId, force, changeChildStatuses } = params;
    // return console.log({ fromDate });
    const status = serviceStatus.find((item) => item.id === serviceStatusId)?.alias || "";
    return dispatch(
      changeProductStatus({
        id: Number(productId),
        fromDate,
        status,
        force,
        changeChildStatuses,
        hideSuccessModal: true,
      })
    );
  };

  // Изменение количества экземпляров продукта
  const handleСhangeProductQuantity = (params: {
    quantity: number;
    fromDate?: string;
    force: boolean;
  }) => {
    const { fromDate, quantity, force } = params;
    return dispatch(
      changeProductQuantity({
        id: Number(productId),
        fromDate,
        quantity,
        force,
        hideSuccessModal: true,
      })
    );
  };

  // Изменение условий пролонгации
  const handleChangeProlongOffering = (params: {
    prolongTypeId: number;
    offeringId: number;
    activationCheck?: boolean;
  }) => {
    const { prolongTypeId, offeringId, activationCheck } = params;
    dispatch(
      changeProlognOffering({
        serviceId: Number(productId),
        prolongTypeId,
        offeringId,
        activationCheck,
      })
    ).then(() => {
      dispatch(fetchProductVersions(Number(productId)));
    });
  };

  const [activeOfferVersion, setActiveOfferVersion] = useState<Offer>();

  // Загрузка предложения по входу и очистка по выходу
  useEffect(() => {
    const id = Number(product?.offering?.id);
    if (id > 0 && onDate) {
      dispatch(
        fetchOfferByIdFromVersions({
          id,
          onDate: toHttpFormatStartOfDay(onDate, ymdDateFormat),
          allObjectsVersions: true,
          withChars: true,
        })
      );
    }
    return () => {
      dispatch(offeringActions.cleanOffer());
    };
  }, [product]);

  // По дефолту activeOfferVersion === offer
  useEffect(() => {
    setActiveOfferVersion(offer);
  }, [offer]);

  // Загрузка вложенных сущностей оффера при смене версии
  useEffect(() => {
    if (activeOfferVersion?.fromDate && activeOfferVersion.toDate && onDate) {
      const { toDate } = activeOfferVersion;
      // Если дата запроса находится в пределах версии, то за fromDate принимается дата запроса (для отображения актуальных версий сущности)
      const fromDate = moment(onDate, ymdDateFormat).isBetween(
        activeOfferVersion.fromDate,
        toDate,
        "day",
        "[]"
      )
        ? toHttpFormatStartOfDay(onDate, ymdDateFormat)
        : activeOfferVersion.fromDate;
      const aggRuleIds = activeOfferVersion.aggregationRules?.map(({ id }) => id);
      if (aggRuleIds?.length) {
        dispatch(
          fetchAggregationRulesFromVersions({
            ids: [...new Set(aggRuleIds)],
            fromDate,
            toDate,
          })
        );
      } else {
        dispatch(aggregationRulesActions.cleanAggregationRules());
      }
      const rateIds: number[] = [];
      const skipTsError = activeOfferVersion.aggregationRules?.forEach(({ serviceRates }) => {
        const skipTsError1 = serviceRates?.forEach(({ id }) => {
          rateIds.push(id!);
        });
      });
      const skipTsError2 = activeOfferVersion.serviceRates?.forEach(({ id }) => {
        rateIds.push(id!);
      });
      if (rateIds?.length) {
        dispatch(
          fetchServiceRatesFromVersions({
            ids: [...new Set(rateIds)],
            fromDate,
            toDate,
          })
        );
      } else {
        dispatch(offeringActions.cleanServiceRates());
      }
      const { serviceProfile } = activeOfferVersion;
      if (serviceProfile.id) {
        dispatch(
          fetchOfferServiceProfileById({
            id: serviceProfile.id,
            onDate: fromDate,
          })
        );
      } else {
        dispatch(offeringActions.cleanServiceProfile());
      }
    }
    return () => {
      dispatch(aggregationRulesActions.cleanAggregationRules());
      dispatch(offeringActions.cleanServiceRates());
      dispatch(offeringActions.cleanServiceProfile());
    };
  }, [activeOfferVersion, onDate]);

  const contextValue: IAccountProductsContainerContext = {
    activeOfferVersion,
    setActiveOfferVersion,
  };

  return (
    <AccountProductsContainerContext.Provider value={contextValue}>
      <div className="product-details-container">
        <ProductHeader loadProductBlockingTypeStatus={loadProductBlockingTypeStatus} />
        {product && (
          <ProductParamsTable
            productParamsForm={productParamsForm}
            minCloseDate={minCloseDate}
            loadProductVersions={loadProductVersions}
            handleCloseProduct={handleCloseProduct}
            handleCancelProduct={handleCancelProduct}
            handleActivateProduct={handleActivateProduct}
            handleСhangeProductStatus={handleСhangeProductStatus}
            handleChangeProlongOffering={handleChangeProlongOffering}
            handleСhangeProductQuantity={handleСhangeProductQuantity}
            loadProduct={loadProduct}
            productOnDate={productOnDate}
          />
        )}
        <ProductsOfferParams />
        <AggregationRulesView isProductDetails />
        <ProductsTarificationParams />
        <ProductsServiceProfileParams />
      </div>
    </AccountProductsContainerContext.Provider>
  );
};
